import { BrowserRouter, Route, Routes } from "react-router-dom"
import { ConfigProvider } from "@/config/context"
import type { LoadedConfig } from "@/config/types"
import { I18nProvider } from "@/i18n"
import { AppShell } from "@/components/Layout"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { AuthProvider } from "@/state/AuthContext"
import { BookmarkProvider } from "@/state/BookmarkContext"
import { FeedProvider } from "@/state/FeedContext"
import { NostrProvider } from "@/state/NostrContext"
import { RelayProvider } from "@/state/RelayContext"
import { SettingsProvider } from "@/state/SettingsContext"
import { ToastProvider } from "@/state/ToastContext"
import { FeedPage } from "@/pages/FeedPage"
import { ExplorePage } from "@/pages/ExplorePage"
import { RelayPage } from "@/pages/RelayPage"
import { NotePage } from "@/pages/NotePage"
import { ProfilePage } from "@/pages/ProfilePage"
import { NotificationsPage } from "@/pages/NotificationsPage"
import { BookmarksPage } from "@/pages/BookmarksPage"
import { SearchPage } from "@/pages/SearchPage"
import { MyProfilePage } from "@/pages/MyProfilePage"
import { SettingsPage } from "@/pages/SettingsPage"
import { NotFoundPage } from "@/pages/NotFoundPage"

/**
 * Provider order matters: configuration → settings → i18n → toasts → relays →
 * auth → relay library → bookmarks → feed → router.
 */
export function App({ loaded }: { loaded: LoadedConfig }) {
  const { config } = loaded
  return (
    <ConfigProvider value={loaded}>
      <SettingsProvider>
        <I18nProvider>
          <ToastProvider>
            <NostrProvider>
              <AuthProvider>
                <RelayProvider>
                  <BookmarkProvider>
                    <FeedProvider>
                      <ErrorBoundary>
                        <BrowserRouter>
                          <Routes>
                            <Route element={<AppShell />}>
                              <Route index element={<FeedPage />} />
                              {config.features.explore && <Route path="explore" element={<ExplorePage />} />}
                              {config.features.notifications && (
                                <Route path="notifications" element={<NotificationsPage />} />
                              )}
                              {config.features.bookmarks && (
                                <Route path="bookmarks" element={<BookmarksPage />} />
                              )}
                              {config.features.search && <Route path="search" element={<SearchPage />} />}
                              <Route path="settings" element={<SettingsPage />} />
                              <Route path="profile" element={<MyProfilePage />} />
                              <Route path="relay/:url" element={<RelayPage />} />
                              <Route path="n/:id" element={<NotePage />} />
                              <Route path="u/:npub" element={<ProfilePage />} />
                              <Route path="*" element={<NotFoundPage />} />
                            </Route>
                          </Routes>
                        </BrowserRouter>
                      </ErrorBoundary>
                    </FeedProvider>
                  </BookmarkProvider>
                </RelayProvider>
              </AuthProvider>
            </NostrProvider>
          </ToastProvider>
        </I18nProvider>
      </SettingsProvider>
    </ConfigProvider>
  )
}
